export interface Article {
  id: string;
  title: string;
  excerpt: string;
  content: string;
  category: string;
  author: string;
  date: string;
  image: string;
  readTime: string;
  tags: string[];
}

export const articles: Article[] = [
  {
    id: 'article-1',
    title: '高柳家的起源：从江户时代走来的匠人之家',
    excerpt: '高柳家的故事始于江户时代中期，第一代家主高柳宗一郎以精湛的木工技艺在京都立足，由此开启了一个匠人家族跨越数百年的传承篇章。',
    content: `<p>高柳家的故事始于江户时代中期（约公元1750年），第一代家主<strong>高柳宗一郎</strong>以精湛的木工技艺在京都西阵地区立足。当时的京都作为日本的文化中心，汇聚了全国最优秀的工匠与艺术家，高柳宗一郎凭借对木材纹理的敏锐感知和一丝不苟的工作态度，迅速在众多工匠中脱颖而出。</p>
<p>宗一郎的工坊最初专注于寺庙建筑中的木雕装饰，其作品以线条流畅、细节精致而著称。他有一句座右铭流传至今：<strong>"木有灵性，匠有心声"</strong>。这句话后来成为高柳家世代相传的家训核心。</p>
<p>到了明治维新时期，第四代家主高柳源次郎顺应时代变革，将家族事业从传统建筑木雕拓展至家具制造领域。他融合西洋家具的功能性与日本传统审美，创造出了一种被称为"和洋折衷"的独特风格，深受当时新兴中产阶级的青睐。</p>
<p>第二次世界大战后，第六代家主高柳健一在面对传统手工艺日渐式微的危机中，做出了一个关键决定：将高柳家的技艺整理成系统的教学体系，开设工艺传习所，向社会开放招生。这一举措不仅确保了技艺的传承，更让高柳家从单一的家族企业转型为文化教育机构。</p>
<p>如今的高柳家已经传至<strong>第九代</strong>，在坚守传统核心价值的同时，积极拥抱现代设计与可持续发展理念，将家族的影响力扩展至家居、茶道、香道、花道等多个生活美学领域。</p>`,
    category: 'brand-story',
    author: '高柳宗明',
    date: '2025-12-15',
    image: 'https://readdy.ai/api/search-image?query=Historic%20Japanese%20wooden%20workshop%20from%20Edo%20period%20with%20traditional%20tools%20and%20half%20finished%20woodcraft%2C%20warm%20candlelight%2C%20aged%20wood%20textures%2C%20historical%20atmosphere%2C%20calligraphy%20scrolls%20on%20wall%2C%20artisan%20heritage%20scene&width=1200&height=600&seq=article-1&orientation=landscape',
    readTime: '8 分钟',
    tags: ['高柳家历史', '江户时代', '匠人传承', '家族起源'],
  },
  {
    id: 'article-2',
    title: '匠心之道：高柳家木工技艺的五大核心秘诀',
    excerpt: '高柳家的木工技艺历经两百余年打磨，形成了选材、干燥、接合、雕刻、涂装五大核心工序的独特体系，每一道工序都蕴含着匠人对完美的执着。',
    content: `<p>高柳家的木工技艺，历经九代人、超过两百七十年的持续打磨与精进，已经形成了一套完整而独特的工艺体系。这套体系可以归纳为五大核心工序，每一道工序都凝聚着匠人数十年的经验与智慧。</p>
<h3>一、选材之道：与木材对话</h3>
<p>高柳家的匠人认为，选材是决定作品灵魂的第一步。每年冬季，当家主会亲自前往日本各地的山林选材。他们偏爱<strong>树龄在80年以上的天然木材</strong>，因为这样的木材质地稳定、纹理优美。选材时，匠人会仔细观察木材的年轮密度、纹理走向、节疤分布，甚至会用指节轻敲木材听其回响，以此判断木材的内在品质。</p>
<h3>二、干燥之道：时间的艺术</h3>
<p>高柳家坚持<strong>自然干燥法</strong>，将木材放置在通风良好的仓库中，经过至少三至五年的自然风干。这一过程虽然漫长，但能够最大限度地保持木材的天然特性，避免人工快速干燥带来的内部应力不均。高柳家有一句谚语："急ぎは木を殺す"（急躁会杀死木材）。</p>
<h3>三、接合之道：不用一钉的智慧</h3>
<p>高柳家最引以为傲的技艺之一就是<strong>木组接合技术</strong>。通过精密的榫卯结构，将不同的木件严丝合缝地连接在一起，全程不使用任何金属钉子或螺丝。这种接合方式不仅美观，更能让家具在数十年甚至上百年的使用中保持稳定。</p>
<h3>四、雕刻之道：刀尖上的舞蹈</h3>
<p>高柳家的雕刻技艺以<strong>"浮造"</strong>技法最为著名——通过特殊的刻刀在木面上创造出微妙的凹凸纹理，使得木纹在不同角度光线下呈现出丰富的层次感。每一件作品上的雕刻图案都经过精心设计，通常取材于自然界中的花草、流水、云霞等元素。</p>
<h3>五、涂装之道：保护与升华</h3>
<p>高柳家使用自制的<strong>天然植物漆</strong>进行涂装。这种漆料由漆树汁液与多种天然颜料调配而成，经过反复涂抹与打磨，最终呈现出温润如玉的质感。完整的涂装工序需要经过至少七次涂抹和六次打磨，耗时超过一个月。</p>`,
    category: 'craftsmanship',
    author: '高柳宗明',
    date: '2025-11-28',
    image: 'https://readdy.ai/api/search-image?query=Close%20up%20of%20Japanese%20master%20craftsman%20hands%20using%20traditional%20woodworking%20tools%20on%20fine%20wood%20surface%2C%20intricate%20wood%20joinery%20detail%2C%20wood%20shavings%2C%20warm%20workshop%20lighting%2C%20artisan%20skill%20demonstration&width=1200&height=600&seq=article-2&orientation=landscape',
    readTime: '12 分钟',
    tags: ['匠心工艺', '木工技艺', '传统工艺', '匠人精神'],
  },
  {
    id: 'article-3',
    title: '茶禅一味：高柳家茶道空间的营造哲学',
    excerpt: '高柳家将茶道视为生活美学的核心表达。本文带您走进高柳家精心打造的茶室空间，感受"和敬清寂"的茶道精神如何在空间中流淌。',
    content: `<p>在高柳家的生活美学体系中，<strong>茶道</strong>占据着无可替代的核心位置。对于高柳家的每一代人来说，茶道不仅是一种饮茶的礼仪，更是一套完整的哲学体系和生活方式。</p>
<p>高柳家现任家主高柳宗明曾这样阐述他对茶道的理解：<strong>"茶道是时间的容器，在方寸茶室中，我们暂时摆脱了日常的喧嚣，回归到最本真的状态。"</strong></p>
<p>高柳家的茶室设计严格遵循<strong>"和敬清寂"</strong>的茶道精神。茶室的入口特意设计得低矮狭窄，称为"躙口"（にじりぐち），所有进入茶室的人都必须弯腰低头，象征着在茶道面前人人平等，不分贵贱。</p>
<p>茶室内部使用高柳家特有的木材与工艺打造。壁龛（床の間）的柱子选用百年桧木，保留其天然的弯曲形态，不做过多的修饰，体现<strong>"侘寂"</strong>（わびさび）美学——在不完美中发现美。</p>
<p>茶室外的庭院同样经过精心设计。一条蜿蜒的石板小径通向茶室，两侧种植苔藓与枫树，造园手法遵循<strong>"露地"</strong>（ろじ）传统，让来访者在步入茶室之前，已经完成了一次心灵的净化。</p>
<p>高柳家每年举办四次正式的茶会，分别在春分、夏至、秋分、冬至举行，每次茶会的主题、花艺、茶具、和果子都会随季节变换，体现"一期一会"（いちごいちえ）的珍重之心。</p>`,
    category: 'lifestyle',
    author: '高柳美智子',
    date: '2025-11-10',
    image: 'https://readdy.ai/api/search-image?query=Traditional%20Japanese%20tea%20room%20interior%20with%20tatami%20mats%20and%20tokonoma%20alcove%2C%20hanging%20scroll%20and%20simple%20flower%20arrangement%2C%20soft%20light%20through%20paper%20screens%2C%20iron%20kettle%20on%20hearth%2C%20serene%20and%20minimal%20atmosphere&width=1200&height=600&seq=article-3&orientation=landscape',
    readTime: '10 分钟',
    tags: ['茶道', '生活美学', '空间设计', '侘寂'],
  },
  {
    id: 'article-4',
    title: '守护无形遗产：高柳家与日本传统工艺振兴运动',
    excerpt: '在日本传统工艺面临传承断层的当下，高柳家积极参与"人间国宝"保护计划，推动多项濒危工艺的复兴与现代化转型。',
    content: `<p>随着现代工业化的快速推进，日本许多传承数百年的传统工艺正面临<strong>后继无人</strong>的严峻挑战。高柳家作为传统工艺世家，深感责任重大，自第七代家主起，便积极投身于传统工艺的保护与振兴事业。</p>
<p>1980年代，高柳家联合京都、金泽、轮岛等地的传统工艺家族，共同发起成立了<strong>"日本传统工艺传承联盟"</strong>。该联盟的核心目标是：为濒危工艺寻找年轻传承人、推动传统工艺与现代设计融合、建立传统工艺的数字化档案。</p>
<p>高柳家每年投入大量资源用于<strong>青年匠人培养计划</strong>。该计划面向全国招募对传统工艺有热情的年轻人，提供为期三年的全额奖学金研修机会。学员在高柳家工坊中，跟随资深匠人系统学习木工、漆艺、金工等传统技艺。迄今为止，该计划已培养了超过<strong>120名</strong>青年匠人，其中不少已经成为各自领域的佼佼者。</p>
<p>在数字化保护方面，高柳家建立了<strong>"高柳技艺数字档案馆"</strong>，通过高清影像、3D扫描等技术手段，将数百种传统工艺的制作流程完整记录下来。这一举措不仅为后世保存了珍贵的技艺资料，也为工艺研究提供了全新的视角。</p>
<p>高柳家还积极探索传统工艺的<strong>当代转化</strong>之路。通过与当代设计师合作，将传统技法应用于现代家居产品设计中，让古老的手艺以全新的面貌走入现代人的生活。高柳宗明常说：<strong>"传统不是一成不变的复制，而是在传承中不断创新。"</strong></p>`,
    category: 'cultural-heritage',
    author: '高柳宗明',
    date: '2025-10-20',
    image: 'https://readdy.ai/api/search-image?query=Young%20Japanese%20apprentice%20learning%20traditional%20lacquerware%20technique%20from%20elderly%20master%20in%20traditional%20workshop%2C%20cultural%20preservation%2C%20intergenerational%20knowledge%20transfer%2C%20warm%20natural%20lighting%2C%20authentic%20craftsmanship%20education&width=1200&height=600&seq=article-4&orientation=landscape',
    readTime: '9 分钟',
    tags: ['文化传承', '工艺振兴', '无形遗产', '青年培养'],
  },
  {
    id: 'article-5',
    title: '高柳家的日常：一日生活中的美学修行',
    excerpt: '在高柳家的理念中，生活本身就是一场持续的审美修行。从清晨的扫除到夜晚的阅读，每一个日常片段都可以成为美的载体。',
    content: `<p>对于高柳家的人来说，<strong>美</strong>不是悬挂在美术馆墙上的画作，也不是锁在玻璃柜中的工艺品——美就在每一个平凡的日常瞬间之中。正如高柳家的家训所言：<strong>"日常即道场，生活即修行"</strong>。</p>
<p><strong>清晨五点三十分</strong>，高柳家的成员们开始了一天的第一个仪式：<strong>扫除</strong>。这不仅仅是清洁，而是一种冥想式的行为。用竹帚轻轻扫过石庭的砂砾，用湿布仔细擦拭木质走廊的每一寸表面——专注于此，心无旁骛。高柳宗明认为，扫除是每天与空间对话的方式，也是修身养性的基本功。</p>
<p><strong>早晨七点</strong>，早餐时间。高柳家的早餐遵循<strong>"一汁三菜"</strong>的传统结构：一碗味噌汤、一份主菜（通常是烤鱼或玉子烧）、两份小菜（当季渍物和煮物），配以白米饭。餐具全部使用高柳家自制的漆器与陶瓷器，食物的色泽与器皿的质感交相辉映。</p>
<p><strong>上午九点至正午</strong>，工作时间。无论是工坊中的匠人，还是负责品牌运营的年轻一代，高柳家的工作节奏都保持着一种<strong>沉稳的专注</strong>。现代的电子邮件与社交媒体管理与传统手工艺的慢节奏并行不悖，形成了一种独特的工作节奏。</p>
<p><strong>下午三点</strong>，茶歇时间。高柳家的下午茶不仅仅是为了休息，更是一种<strong>感官的苏醒仪式</strong>。抹茶的苦、和果子的甜、榻榻米的草香、庭院中传来的风声——这些感官体验交织在一起，构成了高柳家独特的美学记忆。</p>
<p><strong>傍晚六点</strong>，一天的工作结束。高柳家的成员会花一些时间<strong>反思当日的所作所为</strong>，记录在个人的"修行笔记"中。这一习惯源自禅宗的"反省"传统，目的是在不断的自我审视中精进。</p>`,
    category: 'lifestyle',
    author: '高柳美智子',
    date: '2025-10-05',
    image: 'https://readdy.ai/api/search-image?query=Morning%20light%20in%20traditional%20Japanese%20house%20interior%2C%20wooden%20corridor%20overlooking%20zen%20garden%2C%20simple%20breakfast%20setting%20on%20low%20table%2C%20bamboo%20broom%20leaning%20against%20wall%2C%20peaceful%20daily%20life%20scene%2C%20warm%20natural%20atmosphere&width=1200&height=600&seq=article-5&orientation=landscape',
    readTime: '11 分钟',
    tags: ['生活方式', '日常美学', '修行', '禅意生活'],
  },
  {
    id: 'article-6',
    title: '从京都到世界：高柳家品牌的国际化之路',
    excerpt: '高柳家如何在保持传统核心价值的同时，将品牌影响力从京都扩展至全球？本文揭示高柳家品牌国际化背后的战略思考。',
    content: `<p>2010年，高柳家在京都最古老的商业街<strong>三条通</strong>开设了第一家面向公众的品牌体验店<strong>"高柳の舎"</strong>。这标志着高柳家从一个相对封闭的家族工坊，正式迈出了品牌化的第一步。</p>
<p>品牌化的过程并非一帆风顺。最大的挑战在于：<strong>如何在商业化的同时不丢失传统工艺的灵魂？</strong>高柳家给出的答案是"精而非多"。他们严格控制产量，每一件打上高柳家印记的产品都必须经过家主的亲自检验。这一策略在短期内限制了营收，却成功建立起了高柳家"件件精品"的市场口碑。</p>
<p>2015年，高柳家受邀参加<strong>米兰设计周</strong>，这成为品牌国际化的转折点。高柳家在米兰展出的"和风现代家居系列"引起了国际设计界的广泛关注。意大利《Domus》杂志以"东方匠心的当代诠释"为题进行了专题报道。此后，来自巴黎、纽约、伦敦的订单纷至沓来。</p>
<p>2018年，高柳家在<strong>巴黎玛黑区</strong>开设了第一家海外直营店。店铺设计由日本著名建筑师<strong>隈研吾</strong>操刀，以"森林中的茶室"为概念，在巴黎的繁华街区中营造出一方宁静的东方空间。开业首月，到店客流就突破了<strong>一万人次</strong>。</p>
<p>如今，高柳家的产品已销往全球<strong>28个国家和地区</strong>，与超过<strong>50家</strong>国际高端零售商建立了合作关系。在电子商务方面，高柳家的官方线上商城支持<strong>中、日、英、法</strong>四种语言，覆盖全球主要市场。</p>
<p>面对未来，高柳宗明表示：<strong>"我们不会为了扩张而牺牲品质。高柳家的目标从来不是成为最大的品牌，而是成为最值得信赖的、能够跨越世代传承的品牌。"</strong></p>`,
    category: 'brand-story',
    author: '高柳宗明',
    date: '2025-09-18',
    image: 'https://readdy.ai/api/search-image?query=Modern%20Japanese%20brand%20flagship%20store%20interior%20in%20Paris%2C%20minimalist%20wood%20and%20stone%20design%2C%20traditional%20Japanese%20crafts%20elegantly%20displayed%2C%20international%20customers%20browsing%2C%20warm%20ambient%20lighting%2C%20cross%20cultural%20aesthetic%20fusion&width=1200&height=600&seq=article-6&orientation=landscape',
    readTime: '10 分钟',
    tags: ['品牌故事', '国际化', '高柳家', '品牌战略'],
  },
  {
    id: 'article-7',
    title: '四季之色：高柳家天然染色工艺的色彩世界',
    excerpt: '从春天的樱花粉到秋天的柿色，高柳家利用天然植物染料创造出一系列独具韵味的生活器物，让四季的色彩在日常中得以延续。',
    content: `<p>在高柳家的众多工艺中，<strong>天然染色</strong>（草木染め）是一项与自然节律最为紧密相连的技艺。不同于化学染料的鲜艳夺目，天然染色呈现出的是经过时间沉淀后的<strong>内敛而深邃的美</strong>。</p>
<p>高柳家的染色工坊按照季节轮作不同的染料植物。<strong>春天</strong>，使用樱花花瓣和紫草根染出柔和的粉色与淡紫色；<strong>夏天</strong>，使用蓝草（蓼蓝）经过发酵制成靛蓝染料，染出层次丰富的蓝色；<strong>秋天</strong>，使用柿子、栗子皮染出温暖的棕橙色；<strong>冬天</strong>，使用椿树（山茶花）的灰汁染出沉静的灰色。</p>
<p>天然染色最迷人之处在于其<strong>不可复制性</strong>。即使是同一种植物、同一位匠人、同样的工序，不同批次的染色结果也会因水质、温度、湿度等因素而呈现出微妙的差异。高柳家的匠人认为，这不是缺陷，而是<strong>自然的签名</strong>。</p>
<p>染色完成后，布料还需要经过<strong>"日晒固色"</strong>的工序——在通风良好的阴凉处自然晾晒数日，利用空气中的氧气帮助颜色与纤维深度结合。这一过程不能操之过急，高柳家遵循古训："染めは一日、乾かしは七日"（染色只需一日，干燥却需七日）。</p>
<p>近年来，高柳家积极探索天然染色在现代时尚中的应用。2024年与日本知名服装品牌合作推出"四季色谱"限定系列，将传统染色技法应用于现代服饰设计，受到了年轻消费者的热烈追捧。该系列首批<strong>500件</strong>产品在发售后<strong>三小时内</strong>全部售罄。</p>`,
    category: 'craftsmanship',
    author: '高柳花子',
    date: '2025-09-01',
    image: 'https://readdy.ai/api/search-image?query=Natural%20plant%20dyed%20fabric%20swatches%20in%20various%20earthy%20colors%20hanging%20to%20dry%20in%20traditional%20Japanese%20workshop%2C%20indigo%20vat%2C%20natural%20dye%20materials%20in%20wooden%20bowls%2C%20soft%20natural%20light%2C%20artisan%20textile%20craft&width=1200&height=600&seq=article-7&orientation=landscape',
    readTime: '9 分钟',
    tags: ['天然染色', '草木染', '四季', '工艺美学'],
  },
  {
    id: 'article-8',
    title: '高柳宗明访谈：第九代传人眼中的传承与创新',
    excerpt: '在本次深度访谈中，高柳家第九代家主高柳宗明分享了他对家族传承的思考、对传统工艺未来的展望，以及作为新一代家主的责任与使命。',
    content: `<p>在一个深秋的午后，我们走进了高柳家在京都的宅邸，与第九代家主<strong>高柳宗明</strong>先生进行了长达两个小时的深入交流。宗明先生今年<strong>52岁</strong>，接任家主已有<strong>15年</strong>。他身穿一件深蓝色作务衣，神态从容而谦和。</p>
<p><strong>问：作为第九代传人，您如何看待"传承"二字？</strong></p>
<p>宗明：很多人把"传承"理解为"原封不动地传给下一代"，但我认为这是对传承最大的误解。真正的传承，是在理解前人精神内核的基础上，用当代的语言重新诠释。就像河流，水在不断地流动和变化，但河流的方向和本质是不变的。</p>
<p><strong>问：在您接任家主的这15年里，高柳家发生了哪些重要的变化？</strong></p>
<p>宗明：最大的变化是我们从一个"家族内部的事业"，变成了一个"面向社会的事业"。以前高柳家的技艺主要在家族内部传承，外界很难接触到。但现在我们通过工坊开放日、线上课程、社交媒体等渠道，让更多人了解传统工艺的魅力。这是一次观念的转变——技艺不应该被锁在家族的保险柜里，它需要走向社会，接受时代的检验。</p>
<p><strong>问：您如何看待AI和现代科技对传统工艺的影响？</strong></p>
<p>宗明：这是一个很好的问题。我认为科技是工具，不是威胁。我们在数字化档案、线上教学、3D扫描保护等方面已经在使用现代技术。但我始终相信，手工艺中那种<strong>"不可言传只可意会"</strong>的部分，那种匠人的直觉和感受力，是任何机器都无法替代的。</p>
<p><strong>问：对想了解高柳家的人来说，您最想传递的是什么？</strong></p>
<p>宗明：我想说的是，高柳家不只是一个品牌，更是一种<strong>对待生活的态度</strong>。我们希望通过自己的作品和理念，让现代人在快节奏的生活中，有机会停下来，感受一块木头的温度，品味一杯茶的余韵，体会一件手工器物的质感。这些看似微小的事情，其实正是高柳家文化的核心。</p>`,
    category: 'brand-story',
    author: '编辑部',
    date: '2025-08-15',
    image: 'https://readdy.ai/api/search-image?query=Middle%20aged%20Japanese%20master%20craftsman%20in%20traditional%20workshop%2C%20sitting%20on%20wooden%20stool%2C%20calm%20confident%20expression%2C%20surrounded%20by%20handcrafted%20wooden%20pieces%2C%20soft%20window%20light%2C%20authentic%20portrait%20style&width=1200&height=600&seq=article-8&orientation=landscape',
    readTime: '15 分钟',
    tags: ['访谈', '传承', '创新', '家主'],
  },
];

export const getArticlesByCategory = (slug: string): Article[] => {
  return articles.filter((a) => a.category === slug);
};

export const getArticleById = (id: string): Article | undefined => {
  return articles.find((a) => a.id === id);
};